import { distance as levenshteinDistance } from "fastest-levenshtein";

import {
  DestinationPropertyFragment,
  SourcePropertyFragment,
} from "@/apollo/types";

import { normalizationMap } from "./normalization-map";

export function preprocessFieldName(name: string): string {
  let result = name
    .toLowerCase()
    .replace(/[_.\-/]/g, " ") // replace underscores, dots, dashes, slashes with space
    .replace(/[()]/g, "") // remove ( and ) themselves, but KEEP contents
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim();

  // Normalize synonyms
  for (const [pattern, replacement] of Object.entries(normalizationMap)) {
    const regex = new RegExp(`\\b${pattern}\\b`, "gi");
    result = result.replace(regex, replacement);
  }

  return result;
}

function tokenSet(str: string): Set<string> {
  return new Set(str.split(/\s+/));
}

function tokenOverlapScore(source: string, dest: string): number {
  const sourceTokens = tokenSet(source);
  const destTokens = tokenSet(dest);
  const commonTokens = [...sourceTokens].filter((token) =>
    destTokens.has(token),
  );
  const totalTokens = new Set([...sourceTokens, ...destTokens]);
  return (commonTokens.length / totalTokens.size) * 100;
}

function similarityScore(source: string, dest: string): number {
  const maxLength = Math.max(source.length, dest.length);
  const editDistance = levenshteinDistance(source, dest);
  const normalizedEdit = (1 - editDistance / maxLength) * 100;

  const overlap = tokenOverlapScore(source, dest);

  const containsBonus = source.includes(dest) || dest.includes(source) ? 15 : 0;
  const prefixBonus =
    source.startsWith(dest) || dest.startsWith(source) ? 10 : 0;

  // Weighted average
  return 0.6 * normalizedEdit + 0.3 * overlap + containsBonus + prefixBonus;
}

type MatchedPair<Source, Destination> = {
  source: Source;
  destination: Destination;
  score: number;
};

function autoMapFields<Primary, Candidate>(
  primaryFields: { name: string; object: Primary }[],
  candidateFields: { name: string; object: Candidate }[],
  threshold: number,
): MatchedPair<Primary, Candidate>[] {
  const matches: MatchedPair<Primary, Candidate>[] = [];

  for (const primary of primaryFields) {
    let bestMatch: Candidate | null = null;
    let bestScore = 0;

    for (const candidate of candidateFields) {
      const score = similarityScore(primary.name, candidate.name);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = candidate.object;
      }
      if (bestScore >= 95) break; // early exit on very good match
    }

    if (bestMatch && bestScore >= threshold) {
      matches.push({
        source: primary.object,
        destination: bestMatch,
        score: bestScore,
      });
    }
  }

  return matches;
}

export function autoMapFieldsDynamically(
  sourceFields: SourcePropertyFragment[],
  destinationFields: DestinationPropertyFragment[],
  threshold = 66,
): {
  sourceProperty: SourcePropertyFragment;
  destinationProperty: DestinationPropertyFragment;
}[] {
  const useSourceFirst = true; // destinationFields.length >= 50;

  const preparedSourceFields = sourceFields.map((field) => ({
    name: preprocessFieldName(field.name),
    object: field,
  }));

  const preparedDestinationFields = destinationFields.map((field) => ({
    name: preprocessFieldName(field.propertyName),
    object: field,
  }));

  if (useSourceFirst) {
    const matches = autoMapFields(
      preparedSourceFields,
      preparedDestinationFields,
      threshold,
    );
    return matches.map((match) => ({
      sourceProperty: match.source,
      destinationProperty: match.destination,
    }));
  }

  const matches = autoMapFields(
    preparedDestinationFields,
    preparedSourceFields,
    threshold,
  );
  return matches.map((match) => ({
    sourceProperty: match.destination,
    destinationProperty: match.source,
  }));
}

export function autoMapMatchAny(
  sourceFields: SourcePropertyFragment[],
  destinationFields: DestinationPropertyFragment[],
): {
  sourceProperty: SourcePropertyFragment;
  destinationProperty: DestinationPropertyFragment;
}[] {
  const minLength = Math.min(sourceFields.length, destinationFields.length);
  return sourceFields.slice(0, minLength).map((sourceProperty, index) => ({
    sourceProperty,
    destinationProperty: destinationFields[index],
  }));
}
